import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Property } from '../../types';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useFionaChat } from '../../hooks/useFionaChat';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  MessageCircle, 
  Star, 
  TrendingUp, 
  MapPin, 
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Sparkles
} from 'lucide-react';

interface FionaPropertyAnalysisProps {
  property: Property;
  matchScore: number;
}

const FionaPropertyAnalysis: React.FC<FionaPropertyAnalysisProps> = ({
  property,
  matchScore
}) => {
  const { user } = useAuth();
  const { sendMessage, messages, isLoading } = useFionaChat();
  const [analysis, setAnalysis] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const generatePropertyAnalysis = async () => {
    if (!user || isAnalyzing) return;

    setIsAnalyzing(true);
    try {
      const analysisPrompt = `
        Please analyze this property for me based on my preferences and profile. Use this EXACT format:

        Property: ${property.title}
        Location: ${property.address}, ${property.city}, ${property.state}
        Price: $${property.rent}/month | ${property.bedrooms} bed, ${property.bathrooms} bath
        Amenities: ${property.amenities?.join(', ') || 'None listed'}
        Match Score: ${matchScore}%

        **RECOMMENDATION:** [Choose: PURSUE, CONSIDER, or PASS]

        **PROS:**
        • [List only positive aspects, one per bullet point]
        • [Each point should be unique and specific to this property]

        **CONCERNS:**
        • [List only concerns, drawbacks, or red flags]
        • [Include budget concerns, missing amenities, etc.]

        **VALUE ASSESSMENT:**
        [Brief analysis of whether the rent is fair for what you get - size, amenities, location]

        **LOCATION INSIGHTS:**
        [Neighborhood info, commute considerations, nearby amenities, area vibe]

        **FIONA'S THOUGHTS:**
        [Give me your conclusion thoughts in 2-3 sentences. Lean towards recommending "You should reach out" unless this property is way off for them. Be encouraging and focus on next steps. End with actionable advice.]

        Format exactly as shown above with the ** headers **. Keep each section focused and avoid repeating information between sections.
      `;

      await sendMessage(analysisPrompt);
    } catch (error) {
      console.error('Failed to generate property analysis:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Extract Fiona's latest response about this property
  useEffect(() => {
    const latestResponse = messages
      .filter(msg => !msg.isUser && msg.content.length > 100)
      .pop();

    if (latestResponse && latestResponse.content) {
      // Parse the structured analysis
      const analysisData = parseStructuredAnalysis(latestResponse.content);
      setAnalysis(analysisData);
    }
  }, [messages]);

  const parseStructuredAnalysis = (text: string) => {
    const sections = {
      recommendation: extractRecommendation(text),
      pros: extractBulletSection(text, 'PROS:'),
      concerns: extractBulletSection(text, 'CONCERNS:'),
      valueAssessment: extractTextSection(text, 'VALUE ASSESSMENT:'),
      locationInsights: extractTextSection(text, 'LOCATION INSIGHTS:'),
      fionaThoughts: extractTextSection(text, 'FIONA\'S THOUGHTS:'),
      fullResponse: text
    };
    
    return sections;
  };

  const extractRecommendation = (text: string): 'pursue' | 'consider' | 'pass' => {
    const recommendationMatch = text.match(/\*\*RECOMMENDATION:\*\*\s*(PURSUE|CONSIDER|PASS)/i);
    if (recommendationMatch) {
      return recommendationMatch[1].toLowerCase() as 'pursue' | 'consider' | 'pass';
    }
    
    // Fallback to content analysis
    const lowerText = text.toLowerCase();
    if (lowerText.includes('pursue') || lowerText.includes('highly recommend')) {
      return 'pursue';
    } else if (lowerText.includes('pass') || lowerText.includes('not recommend')) {
      return 'pass';
    }
    return 'consider';
  };

  const extractBulletSection = (text: string, sectionHeader: string): string[] => {
    const sectionRegex = new RegExp(`\\*\\*${sectionHeader}\\*\\*([\\s\\S]*?)(?=\\*\\*|$)`, 'i');
    const match = text.match(sectionRegex);
    
    if (match && match[1]) {
      return match[1]
        .split(/\n/)
        .filter(line => line.trim().startsWith('•'))
        .map(line => line.trim().replace(/^•\s*/, ''))
        .filter(item => item.length > 0)
        .slice(0, 4); // Limit to 4 items
    }
    
    return [];
  };

  const extractTextSection = (text: string, sectionHeader: string): string => {
    const sectionRegex = new RegExp(`\\*\\*${sectionHeader}\\*\\*([\\s\\S]*?)(?=\\*\\*|$)`, 'i');
    const match = text.match(sectionRegex);
    
    if (match && match[1]) {
      return match[1].trim().replace(/\n+/g, ' ');
    }
    
    return '';
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'pursue': return 'text-green-500';
      case 'pass': return 'text-red-500';
      default: return 'text-yellow-500';
    }
  };

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'pursue': return <CheckCircle className="w-4 h-4" />;
      case 'pass': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Auto-generate static analysis based on property details with EXACT format
  const staticAnalysis = {
    recommendation: property.rent <= 2500 && property.bedrooms >= 2 ? 'pursue' : property.rent <= 2700 ? 'consider' : 'pass',
    pros: [
      property.rent <= 2500 ? 'Within your $2,500 budget - excellent affordability' : 'Competitive market pricing',
      property.bedrooms >= 2 ? 'Meets your 2-bedroom requirement perfectly' : `${property.bedrooms}-bedroom layout available`,
      `Spacious ${property.square_feet} sq ft provides comfortable living`,
      property.amenities?.length > 3 ? `Premium amenities: ${property.amenities.slice(0, 2).join(', ')}` : 'Well-maintained and move-in ready'
    ],
    concerns: [
      property.rent > 2500 ? `Rent of $${property.rent} exceeds your $2,500 budget by $${property.rent - 2500}` : null,
      property.bedrooms < 2 ? 'Only has 1 bedroom vs your 2-bedroom preference' : null,
      property.bathrooms < 1.5 ? 'Single bathroom may be limiting for guests' : null,
    ].filter(Boolean),
    valueAssessment: property.rent <= 2500 
      ? `Excellent value at $${property.rent}/month for ${property.bedrooms} bed, ${property.bathrooms} bath with ${property.square_feet} sq ft. This pricing is highly competitive for the Louisville area and includes quality amenities that typically command higher rents.`
      : `At $${property.rent}/month, this property is priced above your budget but offers premium features and a prime location. The extra $${property.rent - 2500}/month gets you superior amenities and a desirable neighborhood.`,
    locationInsights: `Located in ${property.city}, this property is in a vibrant neighborhood with a Walk Score of 75+. You'll find grocery stores, cafes, and fitness centers within 0.5 miles. The area has excellent public transit access and is popular with young professionals. Commute to downtown is approximately 15 minutes.`,
    fionaThoughts: property.rent <= 2500 && property.bedrooms >= 2
      ? `This property is a fantastic match for you! It hits all your major criteria - 2 bedrooms, under $2,500, and in a great location. I strongly recommend reaching out to the landlord today to schedule a tour. Properties like this typically receive multiple applications within 48 hours, so don't wait!`
      : property.rent <= 2700
      ? `This is worth serious consideration. Yes, it's $${property.rent - 2500} over your stated budget, but the amenities and location might justify the stretch. I'd suggest scheduling a tour to see if the premium features make it worthwhile. You can always negotiate or keep looking if it doesn't feel right.`
      : `This property exceeds your budget significantly at $${property.rent}/month. Unless you have flexibility in your finances, I'd recommend passing on this one and continuing your search. There are excellent properties in your price range - keep swiping and you'll find your perfect match!`
  };

  if (!analysis && !isAnalyzing) {
    return (
      <div className="space-y-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Property Header */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">🦩</span>
                  <span className="font-semibold text-foreground">Fiona's Take</span>
                </div>
                <Badge variant="secondary" className="text-xs ml-auto">
                  <Star className="w-3 h-3 mr-1" />
                  {matchScore}% Match
                </Badge>
              </div>
              <p className="text-muted-foreground"><strong>Property:</strong> {property.title}</p>
              <p className="text-muted-foreground"><strong>Location:</strong> {property.address}, {property.city}, {property.state}</p>
              <p className="text-muted-foreground"><strong>Price:</strong> ${property.rent}/month | {property.bedrooms} bed, {property.bathrooms} bath</p>
              <p className="text-muted-foreground"><strong>Amenities:</strong> {property.amenities?.join(', ') || 'None listed'}</p>
            </div>
          </Card>

          {/* Recommendation */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className={cn("flex items-center gap-2", getRecommendationColor(staticAnalysis.recommendation))}>
              {getRecommendationIcon(staticAnalysis.recommendation)}
              <span className="font-semibold uppercase">
                RECOMMENDATION: {staticAnalysis.recommendation.toUpperCase()}
              </span>
            </div>
          </Card>

          {/* Pros & Concerns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 bg-green-500/5 border-green-500/20">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <h4 className="font-semibold text-green-700 dark:text-green-400">Why You'll Love It</h4>
              </div>
              <ul className="space-y-2 text-sm">
                {staticAnalysis.pros.map((pro, index) => (
                  <li key={index} className="text-muted-foreground">• {pro}</li>
                ))}
              </ul>
            </Card>

            {staticAnalysis.concerns.length > 0 && (
              <Card className="p-4 bg-amber-500/5 border-amber-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <h4 className="font-semibold text-amber-700 dark:text-amber-400">Keep in Mind</h4>
                </div>
                <ul className="space-y-2 text-sm">
                  {staticAnalysis.concerns.map((concern, index) => (
                    <li key={index} className="text-muted-foreground">• {concern}</li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Value Assessment */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h4 className="font-semibold">Value Assessment</h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {staticAnalysis.valueAssessment}
            </p>
          </Card>

          {/* Location Insights */}
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-primary" />
              <h4 className="font-semibold">Location Insights</h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {staticAnalysis.locationInsights}
            </p>
          </Card>

          {/* Fiona's Bottom Line */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-4 h-4 text-primary" />
              <h4 className="font-semibold">Fiona's Bottom Line</h4>
            </div>
            <p className="text-sm text-foreground leading-relaxed">
              {staticAnalysis.fionaThoughts}
            </p>
          </Card>

          {/* Generate AI Analysis Button */}
          <Button 
            onClick={generatePropertyAnalysis}
            disabled={isAnalyzing}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate Fresh AI Analysis
          </Button>
        </motion.div>
      </div>
    );
  }

  if (isAnalyzing || isLoading) {
    return (
      <div className="text-center py-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 mx-auto mb-4"
        >
          <RefreshCw className="w-8 h-8 text-primary" />
        </motion.div>
        <p className="text-muted-foreground">Fiona is analyzing this property...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {analysis && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {/* Recommendation Header */}
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center gap-3">
              <div className={cn("flex items-center gap-2", getRecommendationColor(analysis.recommendation))}>
                {getRecommendationIcon(analysis.recommendation)}
                <span className="font-semibold capitalize">
                  {analysis.recommendation === 'pursue' ? 'Highly Recommended' :
                   analysis.recommendation === 'pass' ? 'Not Recommended' :
                   'Consider Carefully'}
                </span>
              </div>
              <Badge variant="secondary" className="text-xs">
                <Star className="w-3 h-3 mr-1" />
                AI Analysis
              </Badge>
            </div>
          </Card>

          {/* Pros & Concerns */}
          {(analysis.pros.length > 0 || analysis.concerns.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysis.pros.length > 0 && (
                <Card className="p-4 bg-green-500/5 border-green-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <h4 className="font-semibold text-green-700 dark:text-green-400">Pros</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {analysis.pros.map((pro: string, index: number) => (
                      <li key={index} className="text-muted-foreground">• {pro}</li>
                    ))}
                  </ul>
                </Card>
              )}

              {analysis.concerns.length > 0 && (
                <Card className="p-4 bg-red-500/5 border-red-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <h4 className="font-semibold text-red-700 dark:text-red-400">Concerns</h4>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {analysis.concerns.map((concern: string, index: number) => (
                      <li key={index} className="text-muted-foreground">• {concern}</li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {/* Value & Location Insights */}
          {(analysis.valueAssessment || analysis.locationInsights) && (
            <div className="space-y-3">
              {analysis.valueAssessment && (
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold">Value Assessment</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {analysis.valueAssessment}
                  </p>
                </Card>
              )}

              {analysis.locationInsights && (
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-primary" />
                    <h4 className="font-semibold">Location Insights</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {analysis.locationInsights}
                  </p>
                </Card>
              )}
            </div>
          )}

          {/* Fiona's Thoughts */}
          {analysis.fionaThoughts && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-primary" />
                <h4 className="font-semibold">Fiona's Thoughts</h4>
              </div>
              <div className="text-sm text-muted-foreground leading-relaxed">
                {analysis.fionaThoughts}
              </div>
            </Card>
          )}

          {/* Regenerate Button */}
          <Button 
            onClick={generatePropertyAnalysis}
            disabled={isAnalyzing}
            variant="outline"
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Analysis
          </Button>

        </motion.div>
      )}
    </div>
  );
};

export default FionaPropertyAnalysis;